import React, { useState, useEffect } from 'react';
import api, { isSilentNetworkError } from '../../services/api';
import AdminTable from '../components/AdminTable';
import AdminModal from '../components/AdminModal';
import styles from './VolumeManagement.module.css';
import { PlusIcon } from '../../components/Icons';

function VolumeManagement() {
  const [volumes, setVolumes] = useState([]);
  const [novels, setNovels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVolume, setEditingVolume] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    novel_id: '',
    order_index: 1
  });

  useEffect(() => {
    fetchVolumes();
    fetchNovels();
  }, []);

  const fetchVolumes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/volumes/');
      setVolumes(response.data);
    } catch (error) {
      if (isSilentNetworkError(error)) return;
      console.error('获取卷列表失败:', error);
      alert('获取卷列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchNovels = async () => {
    try {
      const response = await api.get('/novels/');
      setNovels(response.data);
    } catch (error) {
      console.error('获取小说列表失败:', error);
    }
  };

  const handleCreate = () => {
    setEditingVolume(null);
    setFormData({
      title: '',
      novel_id: '',
      order_index: 1
    });
    setShowModal(true);
  };

  const handleEdit = (volume) => {
    setEditingVolume(volume);
    setFormData({
      title: volume.title || '',
      novel_id: volume.novel_id || '',
      order_index: volume.order_index || 1
    });
    setShowModal(true);
  };

  const handleDelete = async (volume) => {
    if (window.confirm(`确定要删除卷 "${volume.title}" 吗？`)) {
      try {
        await api.delete(`/volumes/${volume.id}`);
        fetchVolumes();
        alert('卷删除成功');
      } catch (error) {
        if (isSilentNetworkError(error)) return;
        console.error('删除卷失败:', error);
        alert('删除卷失败');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.novel_id) {
      alert('请填写卷标题和选择所属小说');
      return;
    }

    try {
      if (editingVolume) {
        await api.put(`/volumes/${editingVolume.id}`, formData);
        alert('卷更新成功');
      } else {
        await api.post('/volumes/', formData);
        alert('卷创建成功');
      }
      
      setShowModal(false);
      fetchVolumes();
    } catch (error) {
      if (isSilentNetworkError(error)) return;
      console.error('保存卷失败:', error);
      alert('保存卷失败');
    }
  };

  const columns = [
    { key: 'id', label: 'ID', width: '80px' },
    { key: 'title', label: '卷标题' },
    { 
      key: 'novel', 
      label: '所属小说', 
      render: (novel) => novel?.title || '未知小说'
    },
    { key: 'order_index', label: '排序', width: '80px' },
    { 
      key: 'chapters_count', 
      label: '章节数', 
      width: '100px',
      render: (value, row) => (
        <span className={styles.count}>
          {row.chapters?.length || 0} 章
        </span>
      )
    }
  ];

  return (
    <div className={styles.volumeManagement}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>卷管理</h1>
          <p className={styles.subtitle}>管理小说的卷信息</p>
        </div>
        <button className={styles.createBtn} onClick={handleCreate}>
          <PlusIcon size={18} />
          添加卷
        </button>
      </div>

      <AdminTable
        data={volumes}
        columns={columns}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        emptyMessage="暂无卷数据"
      />

      {showModal && (
        <AdminModal
          title={editingVolume ? '编辑卷' : '添加卷'}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
          size="medium"
        >
          <div className={styles.form}>
            <div className={styles.formGroup}>
              <label className={styles.label}>卷标题 *</label>
              <input
                type="text"
                className={styles.input}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="请输入卷标题"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>所属小说 *</label>
              <select
                className={styles.select}
                value={formData.novel_id}
                onChange={(e) => setFormData({ ...formData, novel_id: e.target.value })}
                required
              >
                <option value="">请选择小说</option>
                {novels.map(novel => (
                  <option key={novel.id} value={novel.id}>
                    {novel.title} - {novel.author}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>排序序号</label>
              <input
                type="number"
                className={styles.input}
                value={formData.order_index}
                onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 1 })}
                min="1"
                placeholder="1"
              />
              <div className={styles.inputHint}>
                数字越小排序越靠前
              </div>
            </div>
          </div>
        </AdminModal>
      )}
    </div>
  );
}

export default VolumeManagement;
